/** @jsx jsx */
import React from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import { Link } from 'react-router-dom';

import {
  ChallengePhaseType,
  ChallengeResultType,
  ChallengeTypeType,
  ChallengesType,
  ContentType,
  ICustomTheme,
} from 'interfaces';
import {
  ChallengeDeclareButton,
  CustomButtonType,
  MainButton,
  PrimaryButton,
  TertiaryButton,
} from 'components';
import { postUserParticipation } from 'actions/challenge.actions';
import { challengeSelector } from 'reducers/challenge.reducer';
import {
  CHALLENGE,
  CHALLENGE_PHASE,
  RESULT_STATUS,
} from 'constants/challenges';
import { LOADING_STATUS } from 'constants/status';
import { ROUTES } from '../constants';
import { currentContentSelector } from 'reducers/content.reducer';
import { TCurrentGlobalViewWidgetSet } from 'interfaces/widgets.types';

type ParticipateDetailsButtonsTypes = {
  currentGlobalViewWidgetSet?: TCurrentGlobalViewWidgetSet;
  challenge: ChallengesType;
  challengeResults?: ChallengeResultType;
  challengeType: ChallengeTypeType;
  currentPhase?: ChallengePhaseType | null;
  half?: boolean;
  hasDetailsButton?: boolean;
  isDenied: boolean;
  isParticipating?: boolean;
  buttonSize?: CustomButtonType['size'];
};

const StyledPrimaryButton = styled(MainButton)<{ theme: ICustomTheme }>(
  ({ theme }) => ({
    margin: theme.spacing(4, 'auto'),
    '&.half': {
      margin: theme.spacing(0, 2),
    },
  }),
);

const ParticipateDetailsButtons = ({
  currentGlobalViewWidgetSet,
  challenge,
  challengeResults,
  challengeType,
  currentPhase,
  half = false,
  hasDetailsButton = true,
  isDenied,
  isParticipating,
  buttonSize = 'md',
}: ParticipateDetailsButtonsTypes): JSX.Element | null => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status } = useSelector(challengeSelector(challenge?.id));
  const content = useSelector(currentContentSelector);
  const contentId = content?.id;

  const handleParticipation = (
    challengeId: number,
    isParticipating: boolean,
    contentId: number | undefined,
  ): void => {
    dispatch(
      postUserParticipation.request({
        challengeId,
        isParticipating,
        contentId,
        currentGlobalViewWidgetSet,
      }),
    );
  };
  /**
   * Classic or Photo : only 'tell me more'/'ranking' button
   *
   * Deal:
   *   BEFORE_SIGN_IN: tell me more
   *   DURING_SIGN_IN:
   *     undefined: 3 buttons
   *     signed in/refused: tell me more
   *   BEFORE_CHALLENGE: tell me more
   *   DURING_CHALLENGE: tell me more
   *   AFTER_CHALLENGE:
   *     with results AND results visible: ranking
   *     without results OR results not visible: tell me more
   */
  const detailsButtonsTitle =
    !isDenied &&
    (currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE ||
      (currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE &&
        challengeResults?.resultValue))
      ? t('challenges.detailsAndResults')
      : t('challenges.tell_me_more');
  const isDetailsButtonDisplayed = hasDetailsButton && !isDenied;

  const detailsButton = (
    // TODO: clean accesses routes v2 / v1
    <StyledPrimaryButton
      btnType="button"
      size={buttonSize}
      decoration="none"
      component={Link}
      className={half && 'half'}
      to={`${ROUTES.CHALLENGE}/${challenge?.id}`}
    >
      {detailsButtonsTitle}
    </StyledPrimaryButton>
  );

  if (challengeType === CHALLENGE.LIST) {
    const hasChallengeResults = !!challenge?.challengeResults?.length;
    const hasValidatedOrWaitingDeclarations =
      !!challenge.countDeclarationsValidated ||
      !!challenge.countDeclarationsWaiting;

    /* The isSeeMyDeclarationButtonDisplayed variable is defined for the 'Voir ma déclaration' button */
    const isSeeMyDeclarationButtonDisplayed =
      currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE &&
      hasChallengeResults &&
      !challenge.isDeclarationMultiple;

    /* The isIDeclareButtonDisplayed variable is defined for the 'Je déclare' button */
    const isIDeclareButtonDisplayed =
      !isDenied &&
      currentPhase !== CHALLENGE_PHASE.AFTER_CHALLENGE &&
      (challenge.isDeclarationMultiple || !hasValidatedOrWaitingDeclarations);

    return (
      <React.Fragment>
        {isSeeMyDeclarationButtonDisplayed && (
          <PrimaryButton
            btnType="button"
            size={buttonSize}
            color="infoInverted"
            decoration="none"
            component={Link}
            className={half && 'half'}
            to={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}/${challenge.id}`}
          >
            {t('challenges.seeMyDeclaration')}
          </PrimaryButton>
        )}
        {isDetailsButtonDisplayed && detailsButton}
        {isIDeclareButtonDisplayed && (
          <ChallengeDeclareButton
            challenge={challenge}
            buttonSize={buttonSize}
          />
        )}
      </React.Fragment>
    );
  }

  if (
    challengeType === CHALLENGE.DEAL &&
    currentPhase === CHALLENGE_PHASE.DURING_SIGN_IN &&
    isParticipating === undefined &&
    !isDenied &&
    (challenge?.inscriptionsLeft === undefined ||
      challenge?.inscriptionsLeft > 0)
  ) {
    return (
      <React.Fragment>
        <TertiaryButton
          size={buttonSize}
          type="button"
          color="infoInverted"
          onClick={(): void =>
            handleParticipation(challenge.id, false, contentId)
          }
          isDisabled={status === LOADING_STATUS.PENDING}
          isLoading={status === LOADING_STATUS.PENDING}
        >
          {challenge.dealRefuseBtn || t('challenges.i_decline')}
        </TertiaryButton>

        {isDetailsButtonDisplayed && detailsButton}

        <PrimaryButton
          size={buttonSize}
          type="button"
          onClick={(): void =>
            handleParticipation(challenge.id, true, contentId)
          }
          isDisabled={status === LOADING_STATUS.PENDING}
          isLoading={status === LOADING_STATUS.PENDING}
        >
          {challenge.dealAcceptBtn || t('challenges.i_hurry_up')}
        </PrimaryButton>
      </React.Fragment>
    );
  }

  return isDetailsButtonDisplayed ? detailsButton : null;
};

export default ParticipateDetailsButtons;
